import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import FacebookAdsService, {
  FacebookConnection,
  FacebookCampaign,
  FacebookAdSet,
} from '../../services/facebookAdsService';

const FacebookAdsAdSets: React.FC = () => {
  const [connections, setConnections] = useState<FacebookConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<FacebookConnection | null>(null);
  const [campaigns, setCampaigns] = useState<FacebookCampaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<string>('all');
  const [adsets, setAdsets] = useState<FacebookAdSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    if (selectedConnection) {
      loadCampaigns();
    }
  }, [selectedConnection]);

  useEffect(() => {
    if (selectedConnection && selectedCampaign !== 'all') {
      loadAdSets();
    } else if (selectedCampaign === 'all' && selectedConnection && campaigns.length > 0) {
      loadAllAdSets();
    }
  }, [selectedCampaign, selectedConnection, campaigns]);

  const loadConnections = async () => {
    try {
      const response = await FacebookAdsService.listConnections();
      if (response.success && response.data) {
        setConnections(response.data);
        if (response.data.length > 0 && !selectedConnection) {
          setSelectedConnection(response.data[0]);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar conexões:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    if (!selectedConnection) return;

    try {
      const response = await FacebookAdsService.listCampaigns(selectedConnection.account_id.toString());
      if (response.success && response.data) {
        setCampaigns(response.data);
        // Se "Todas as Campanhas" estiver selecionado, carregar todos os adsets
        if (selectedCampaign === 'all') {
          setTimeout(() => loadAllAdSets(), 100);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    }
  };

  const loadAdSets = async () => {
    if (!selectedConnection || selectedCampaign === 'all') return;

    setLoading(true);
    try {
      const response = await FacebookAdsService.getAdSets(selectedCampaign);
      if (response.success && response.data) {
        setAdsets(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar adsets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllAdSets = async () => {
    if (!selectedConnection) return;

    setLoading(true);
    try {
      // Se campaigns ainda não estiver carregado, carregar primeiro
      let campaignsToUse = campaigns;
      if (campaignsToUse.length === 0) {
        const campaignsResponse = await FacebookAdsService.listCampaigns(selectedConnection.account_id.toString());
        if (campaignsResponse.success && campaignsResponse.data) {
          campaignsToUse = campaignsResponse.data;
          setCampaigns(campaignsToUse);
        }
      }

      const allAdSets: FacebookAdSet[] = [];
      for (const campaign of campaignsToUse) {
        const response = await FacebookAdsService.getAdSets(campaign.id.toString());
        if (response.success && response.data) {
          allAdSets.push(...response.data);
        }
      }
      setAdsets(allAdSets);
    } catch (error) {
      console.error('Erro ao carregar adsets:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar adsets', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (adsetId: number) => {
    try {
      const response = await FacebookAdsService.pauseAdSet(adsetId.toString());
      if (response.success) {
        setSnackbar({ open: true, message: 'AdSet pausado', severity: 'success' });
        if (selectedCampaign === 'all') {
          loadAllAdSets();
        } else {
          loadAdSets();
        }
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const handleActivate = async (adsetId: number) => {
    try {
      const response = await FacebookAdsService.activateAdSet(adsetId.toString());
      if (response.success) {
        setSnackbar({ open: true, message: 'AdSet ativado', severity: 'success' });
        if (selectedCampaign === 'all') {
          loadAllAdSets();
        } else {
          loadAdSets();
        }
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const handleDelete = async (adsetId: number) => {
    if (!window.confirm('Tem certeza que deseja deletar este AdSet?')) return;

    try {
      const response = await FacebookAdsService.deleteAdSet(adsetId.toString());
      if (response.success) {
        setSnackbar({ open: true, message: 'AdSet deletado', severity: 'success' });
        if (selectedCampaign === 'all') {
          loadAllAdSets();
        } else {
          loadAdSets();
        }
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const handleArchive = async (adsetId: number) => {
    try {
      const response = await FacebookAdsService.archiveAdSet(adsetId.toString());
      if (response.success) {
        setSnackbar({ open: true, message: 'AdSet arquivado', severity: 'success' });
        if (selectedCampaign === 'all') {
          loadAllAdSets();
        } else {
          loadAdSets();
        }
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const handleDuplicate = async (adsetId: number, name: string) => {
    const newName = prompt(`Nome para a cópia de "${name}":`);
    if (!newName || !newName.trim()) return;

    try {
      const response = await FacebookAdsService.duplicateAdSet(adsetId.toString(), newName.trim());
      if (response.success) {
        setSnackbar({ open: true, message: 'AdSet duplicado', severity: 'success' });
        if (selectedCampaign === 'all') {
          loadAllAdSets();
        } else {
          loadAdSets();
        }
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE': return 'success';
      case 'PAUSED': return 'warning';
      case 'DELETED': case 'DISAPPROVED': return 'error';
      case 'ARCHIVED': return 'default';
      default: return 'default';
    }
  };

  const formatCurrency = (value: number | null) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  if (loading && adsets.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Conjuntos de Anúncios (AdSets)
        </Typography>
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Conta</InputLabel>
            <Select
              value={selectedConnection?.account_id || ''}
              onChange={(e) => {
                const value = e.target.value;
                const conn = connections.find((c) => c.account_id === parseInt(value.toString()));
                setSelectedConnection(conn || null);
              }}
              label="Conta"
            >
              {connections.map((conn) => (
                <MenuItem key={conn.account_id} value={conn.account_id}>
                  {conn.account_name || `Conta ${conn.account_id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Campanha</InputLabel>
            <Select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              label="Campanha"
            >
              <MenuItem value="all">Todas as Campanhas</MenuItem>
              {campaigns.map((campaign) => (
                <MenuItem key={campaign.id} value={campaign.id.toString()}>
                  {campaign.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={() => {
              if (selectedCampaign === 'all') {
                loadAllAdSets();
              } else {
                loadAdSets();
              }
            }} 
            disabled={loading}
          >
            Atualizar
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>AdSet</TableCell>
              <TableCell align="right">Status</TableCell>
              <TableCell align="right">Orçamento Diário</TableCell>
              <TableCell align="right">Orçamento Total</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {adsets.map((adset) => (
              <TableRow key={adset.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {adset.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {adset.id}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip label={adset.status} color={getStatusColor(adset.status) as any} size="small" />
                </TableCell>
                <TableCell align="right">{formatCurrency(adset.daily_budget)}</TableCell>
                <TableCell align="right">{formatCurrency(adset.lifetime_budget)}</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    {adset.status === 'ACTIVE' ? (
                      <Tooltip title="Pausar">
                        <IconButton size="small" onClick={() => handlePause(adset.id)} color="warning">
                          <PauseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Ativar">
                        <IconButton size="small" onClick={() => handleActivate(adset.id)} color="success">
                          <PlayIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Duplicar">
                      <IconButton size="small" onClick={() => handleDuplicate(adset.id, adset.name)} color="primary">
                        <DuplicateIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Arquivar">
                      <IconButton size="small" onClick={() => handleArchive(adset.id)} color="default">
                        <ArchiveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deletar">
                      <IconButton size="small" onClick={() => handleDelete(adset.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {adsets.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">Nenhum AdSet encontrado</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacebookAdsAdSets;


